import React, { useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Eye, EyeOff, Loader2, User, Truck, Building, Shield, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

const BG_IMAGE = "https://images.unsplash.com/photo-1771775751121-3091d79073d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBibGFjayUyMGNoYXVmZmV1ciUyMGNhciUyMGxpbW91c2luZSUyMG5pZ2h0fGVufDB8fHx8MTc3NTQ4MDE1N3ww&ixlib=rb-4.1.0&q=85";

const roleConfig = {
  admin: { icon: Shield, label: "Administrator", title: "Admin Portal", description: "Access system administration & management", dashboard: "/admin" },
  staff: { icon: User, label: "Staff Member", title: "Staff Portal", description: "Access booking management & dispatch", dashboard: "/staff" },
  client: { icon: Building, label: "Client", title: "Client Portal", description: "Access your bookings and invoices", dashboard: "/client" },
  driver: { icon: Truck, label: "Driver", title: "Driver Portal", description: "Access your assigned jobs", dashboard: "/driver" }
};

const RoleLoginPage = () => {
  const { role } = useParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const config = roleConfig[role] || roleConfig.client;
  const Icon = config.icon;

  React.useEffect(() => {
    if (user) {
      navigate(roleConfig[user.role]?.dashboard || "/dashboard");
    }
  }, [user, navigate]);

  // Load remembered email on mount
  React.useEffect(() => {
    const savedEmail = localStorage.getItem(`sky_remember_${role}`);
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, [role]);

  if (!roleConfig[role]) {
    return (
      <div className="min-h-screen bg-[#0b0b0f] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">Invalid login type</p>
          <Link to="/login" className="text-[#C8A95B] hover:underline text-sm">Go to main login</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Handle remember me
    if (rememberMe) {
      localStorage.setItem(`sky_remember_${role}`, email);
    } else {
      localStorage.removeItem(`sky_remember_${role}`);
    }

    const result = await login(email, password, role);
    setLoading(false);

    if (result.success) {
      if (result.user.role !== role) {
        setError(`This account is registered as ${result.user.role}, not ${role}. Please use the correct login page.`);
        return;
      }
      const from = location.state?.from?.pathname;
      navigate(from || config.dashboard);
    } else {
      setError(result.error || "Invalid email or password");
    }
  };

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay, ease: [0.25, 0.1, 0.25, 1] }
  });

  return (
    <div className="min-h-screen flex" data-testid="role-login-page">
      {/* Left — Hero Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img src={BG_IMAGE} alt="Luxury chauffeur vehicle" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/80" />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative z-10 flex flex-col justify-end h-full p-12 pb-16"
        >
          <p className="text-[#C8A95B] uppercase tracking-[0.25em] text-xs font-semibold mb-3">Premium Chauffeur Service</p>
          <h1 className="text-white text-4xl font-bold leading-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Arrive in Style,<br />Every Time.
          </h1>
          <p className="text-white/60 mt-4 max-w-md text-sm leading-relaxed">
            Limo Near Me delivers executive ground transportation across Australia — airport pickups, corporate travel, and special occasions.
          </p>
        </motion.div>
      </div>

      {/* Right — Login Form */}
      <div className="w-full lg:w-1/2 bg-[#0b0b0f] flex flex-col items-center justify-center px-6 py-12 relative">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        <div className="w-full max-w-sm relative z-10">
          {/* Back link */}
          <motion.div {...fadeUp(0)}>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-white/30 hover:text-white/60 text-xs mb-8 transition-colors" data-testid="back-to-login-link">
              <ArrowLeft className="h-3.5 w-3.5" />
              All login options
            </Link>
          </motion.div>

          {/* Logo */}
          <motion.div {...fadeUp(0.05)} className="text-center mb-10">
            <Link to="/" className="inline-block">
              <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-14 w-auto mx-auto" />
            </Link>
          </motion.div>

          {/* Role badge + Header */}
          <motion.div {...fadeUp(0.12)} className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C8A95B]/10 border border-[#C8A95B]/20 mb-4">
              <Icon className="h-3.5 w-3.5 text-[#C8A95B]" />
              <span className="text-[#C8A95B] text-xs font-medium">{config.label}</span>
            </div>
            <h2 className="text-white text-2xl font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>{config.title}</h2>
            <p className="text-white/35 text-sm mt-1.5">{config.description}</p>
          </motion.div>

          {/* Form */}
          <motion.form {...fadeUp(0.2)} onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error-message">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-white/50 text-xs font-medium uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#C8A95B]/50 focus:ring-[#C8A95B]/20 h-11"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-white/50 text-xs font-medium uppercase tracking-wider">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#C8A95B]/50 focus:ring-[#C8A95B]/20 pr-10 h-11"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Remember Me + Forgot */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-white/20 data-[state=checked]:bg-[#C8A95B] data-[state=checked]:border-[#C8A95B]"
                  data-testid="remember-me-checkbox"
                />
                <label htmlFor="remember" className="text-white/40 text-xs cursor-pointer select-none">
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password" className="text-white/25 hover:text-[#C8A95B] text-xs transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#C8A95B] text-black hover:bg-[#d4b96a] rounded-lg h-11 font-semibold text-sm transition-all duration-300"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </motion.form>

          {(role === "client" || role === "driver") && (
            <motion.div {...fadeUp(0.35)} className="mt-6 text-center text-sm">
              <span className="text-white/30">Don't have an account? </span>
              <Link to="/register" className="text-[#C8A95B] hover:text-[#dbbf6f] transition-colors" data-testid="signup-link">
                Sign up
              </Link>
            </motion.div>
          )}

          <p className="text-center text-[10px] text-white/15 mt-12">
            &copy; 2026 Limo Near Me &middot; Australia Wide Car Services Pty Ltd
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoleLoginPage;
